import Select, { type GroupBase, type Props } from 'react-select';

type StyledPlotSelectProps = { maxWidth?: number; height?: string };

export const StyledPlotSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  maxWidth,
  height,
  ...props
}: Props<Option, IsMulti, Group> & StyledPlotSelectProps) => {
  return (
    <Select
      classNamePrefix={props.classNamePrefix}
      isSearchable={props.isSearchable ?? false}
      styles={{
        container: (prev) => ({
          ...prev,
          ...(maxWidth
            ? { maxWidth: `${maxWidth}px`, width: '100%' }
            : { width: '100%' }),
          ...(height ? { height } : {}),
          fontSize: '12px',
          fontWeight: 400,
        }),
        control: (prev, { isFocused }) => ({
          ...prev,
          minHeight: height || '24px',
          height: '100%',
          backgroundColor: 'rgb(var(--ui-ink-rgb) / 0.02)',
          color: 'var(--ui-text-normal)',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: isFocused
            ? 'var(--ui-border-color-focus)'
            : 'rgb(var(--ui-ink-rgb) / 0.08)',
          borderRadius: 2,
          boxShadow: 'none',
          '&:hover': {
            borderColor: isFocused
              ? 'var(--ui-border-color-focus)'
              : 'var(--ui-border-color-hover)',
          },
        }),
        valueContainer: (prev) => ({
          ...prev,
          padding: '0 8px',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          overflow: 'hidden',
        }),
        placeholder: (prev) => ({
          ...prev,
          margin: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          pointerEvents: 'none',
          color: 'var(--ui-text-secondary)',
        }),
        indicatorsContainer: (prev) => ({
          ...prev,
          height: '100%',
          svg: { width: '12px', height: '12px' },
        }),
        clearIndicator: (prev) => ({
          ...prev,
          color: 'var(--ui-text-secondary)',
          padding: '4px',
          cursor: 'pointer',
          ' svg': {},
          ':hover': {
            color: 'var(--ui-text-normal)',
          },
        }),
        indicatorSeparator: (prev) => ({
          ...prev,
          display: 'none',
        }),
        dropdownIndicator: (prev) => ({
          ...prev,
          color: 'var(--ui-text-secondary)',
          ' svg': {
            width: '12px',
            height: '12px',
          },
        }),
        menu: (prev) => ({
          ...prev,
          borderRadius: 0,
          ...(maxWidth
            ? { minWidth: `${maxWidth}px` }
            : { width: 'fit-content', minWidth: '100px' }),
          position: 'absolute',
          left: 0,
          right: 0,
          zIndex: 3,
          backgroundColor: 'var(--ui-surface-floating)',
          border: '1px solid var(--ui-border-floating)',
        }),
        group: (prev) => ({
          ...prev,
          paddingBottom: 0,
          marginBottom: 0,
        }),
        groupHeading: (prev) => ({
          ...prev,
          padding: '0 .5rem',
          color: 'var(--ui-text-muted)',
        }),
        option: (prev, { isSelected, isFocused }) => {
          const backgroundColor = (() => {
            if (isSelected) return 'var(--ui-surface-overlay)';
            if (isFocused) return 'var(--ui-surface-hover)';
            return 'transparent';
          })();
          const optionColor = (() => {
            if (isSelected) return 'var(--ui-text-normal)';
            return 'var(--ui-text-normal)';
          })();
          return {
            ...prev,
            backgroundColor,
            color: optionColor,
            fontSize: '12px',
            fontWeight: 400,
            padding: '8px 12px',
            minHeight: '32px',
            lineHeight: '16px',
            cursor: 'pointer',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            ':active': {
              backgroundColor: 'var(--ui-surface-active)',
            },
            ...(isSelected && {
              ':hover': {
                backgroundColor: 'var(--ui-surface-active)',
              },
            }),
          };
        },
        singleValue: (prev) => ({
          ...prev,
          height: '100%',
          lineHeight: '100%',
          color: 'var(--ui-text-normal)',
        }),
        multiValue: (prev) => ({
          ...prev,
          fontSize: '11px',
          margin: '2px 4px 2px 0',
          backgroundColor: 'rgb(var(--ui-ink-rgb) / 0.05)',
        }),
        multiValueLabel: (prev) => ({
          ...prev,
          fontSize: '11px',
          padding: '4px',
          lineHeight: '1.1',
          color: 'var(--ui-text-normal)',
        }),
        multiValueRemove: (prev) => ({
          ...prev,
          fontSize: '11px',
          padding: '2px 4px',
        }),
        input: (prev) => ({
          ...prev,
          height: '20px',
          margin: 0,
          padding: 0,
          color: 'var(--ui-text-normal)',
          '> .metric-switch__input': {
            height: '20px',
          },
        }),
      }}
      {...props}
    />
  );
};
